import { FC, useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonTextarea, useIonAlert } from '@ionic/react';
import { isPlatform } from '../../utils/isPlatform';
import { IonTextareaCustomEvent, TextareaInputEventDetail } from '@ionic/core';
import { INote } from './INote';
import { IAttachment } from './attachments/IAttachment';
import { PrivateCheckbox } from '../../components/PrivateCheckbox';
import { Menu, IAction } from '../../components/Menu';
import { DeleteIcon, PaperclipIcon, UploadIcon } from '../../theme/Icons';
import { AttachmentList } from './attachments/AttachmentList';
import { DrawerSectionHeading } from '../../components/DrawerSectionHeading';
import { Divider } from '../../components/Divider';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useToast } from '../../components/useToast';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { FileUploader, IUploadCompleteInfo, IUploadingFile } from './attachments/FileUploader';
import { PaywallAwareButton } from '../../components/PaywallAwareButton';
import { IFreeTierLimits } from '../../innerhive-plus/revenue-cat/IFreeTierLimits';
import { NoteTemplatePicker } from './note-templates/NoteTemplatePicker';
import { TemplateProvider } from './note-templates/TemplateProvider';
import { INoteTarget } from './INoteTarget';
import { CareMapNoteTracker } from '../model/CareMapNoteTracker';
import { usePlaceCursor } from '../../utils/usePlaceCursor';
import { TextArea } from '../../components/TextArea';
import { LinkText } from '../../components/LinkText';
import { TagsField } from '../../tags/TagsField';

const maxFileSizeMB = 50;
// this is the actual file size we validate against
// with some padding, so that a ${maxFileSizeMB}MB file on disk, that is say ${maxFileSizeMB}.01 MB in memory
// when we validate it, doesn't throw
const maxFileSize = (maxFileSizeMB + .5) * 1024 * 1024;

const isDesktop = isPlatform('desktop');
const useStyles = createUseStyles({
    dropZone: {
        padding: 2,
        borderRadius: 5,
        border: '2px dashed transparent'
    },

    dragAccept: {
        padding: 2,
        borderRadius: 5,
        border: '2px dashed var(--ion-color-success)'
    },

    browseLink: {
        display: 'flex',
        cursor: 'pointer',
        color: 'var(--ion-color-primary)'
    },

    wrapper: {
        gap: 16,
        display: 'flex',
        flexDirection: 'column'
    },

    attachmentArea: isDesktop ? {
        minHeight: 170,

        '& hr': {
            width: '100%',
            marginBlockStart: -6
        },

        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    } : {
        minHeight: 70,
        paddingBottom: 6,

        '& hr': {
            marginBlockStart: -6
        }
    },

    addAttachments: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',

        '& > div': {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    }
});

export type INoteEditProps = {
    note: INote;
    isAdd?: boolean;
    canEdit: boolean;
    noteTarget: INoteTarget;
    cursorLocation?: number;
    canDelete?: boolean;
    hasPrivateCheckbox?: boolean;
    hasTitle?: boolean;
    excludeMenu?: boolean;
    autoOpenFileChooser?: boolean;
    includeTemplates?: boolean;
    addAttachmentRequiresInnerhivePlus?: (limits: IFreeTierLimits) => boolean;
    noteChanged: (updatedNote: INote, noteLabel?: string) => void;
    noteDeleted?: (deletedNote: INote) => void;
};

export const NoteForm: FC<INoteEditProps> = ({
    note,
    isAdd,
    canEdit,
    noteTarget,
    cursorLocation,
    noteChanged,
    noteDeleted,
    canDelete,
    hasPrivateCheckbox,
    hasTitle,
    excludeMenu,
    autoOpenFileChooser,
    includeTemplates,
    addAttachmentRequiresInnerhivePlus
}) => {
    const classes = useStyles();
    const titleRef = useRef<HTMLIonTextareaElement>(null);
    const textareaRef = useRef<HTMLIonTextareaElement>(null);

    const tracker = useInstance(CareMapNoteTracker);

    const [showToast] = useToast();

    const uploader = useInstance(FileUploader);
    useSubscription(() => uploader);

    const templates = useInstance(TemplateProvider).getTemplates(noteTarget);

    const placeCursor = usePlaceCursor();

    const [presentAlert] = useIonAlert();

    const [internalCursorLocation, setInternalCursorLocation] = useState<number | undefined>(cursorLocation);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (isAdd && (isPlatform('desktop') || isPlatform('capacitor'))) {
                // if isAdd and there's a focusLocation
                if (internalCursorLocation && textareaRef.current) {
                    placeCursor(textareaRef, internalCursorLocation, true);
                    setInternalCursorLocation(undefined);
                } else if (titleRef.current) {
                    titleRef.current.setFocus();
                }
            }
        }, 350);
        return () => clearTimeout(timer);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onUploadComplete = (fileInfo: IUploadCompleteInfo) => {
        tracker.onFileUploaded(fileInfo, noteTarget);
    };

    const onDrop = async (files: File[]) => {
        const uploadedFiles = await uploader.upload(files, onUploadComplete);
        noteChanged({
            ...note,
            attachments: [
                ...note.attachments,
                ...uploadedFiles
            ]
        });
    };

    const rejected = (rejections: FileRejection[]) => {
        const files = rejections.map(r => r.file.name);
        const message = `Unable to attach ${files.join(', ')}. Files must be under ${maxFileSizeMB} MB.`;
        showToast({ message, duration: 5000 });
    };

    const {getRootProps, getInputProps, open, isDragAccept} = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxSize: maxFileSize,
        onDropAccepted: onDrop,
        onDropRejected: rejected
    });

    useEffect(() => {
        if (autoOpenFileChooser) {
            setTimeout(open, 350);
        }
    }, [autoOpenFileChooser]); // eslint-disable-line react-hooks/exhaustive-deps

    // currently, if notes are private and you can't edit, then you also can't view them,
    // in which case we want to render nothing to avoid wasted whitespace in the drawer
    if (note.isPrivate && !canEdit) {
        return null;
    }

    const onNoteTitleChanged = (value: any) => {
        noteChanged({...note, title: value });
    };

    const onNoteHtmlChanged = (e: IonTextareaCustomEvent<TextareaInputEventDetail>) => {
        noteChanged({...note, html: e.target.value || '' });
    };

    const onPrivacyChanged = (isPrivate: boolean) => {
        noteChanged({...note, isPrivate });
    };

    const onTagsChanged = (tags: string[]) => {
        noteChanged({...note, tags });
    };

    const deleteNote = () => {
        const destructiveRole = 'destructive';
        presentAlert({
            header: `Delete Note?`,
            message: `This note will be deleted and cannot be recovered.`,
            cssClass: 'drawer-alert',
            buttons: [{
                text: 'Delete',
                role: destructiveRole,
                handler: () => {
                    noteDeleted!(note);
                }
            }, {
                text: 'Cancel',
                role: 'cancel'
            }]
        });
    };

    const uploadingAttachments: IUploadingFile[] = uploader.uploadingFiles.filter(f => !f.isUploaded);
    const attachments: IAttachment[] = [
        ...note.attachments,
        // and files that are uploaded, but haven't been saved to the note yet,
        // because we are waiting for the rest to finish
        ...uploader.uploadingFiles.filter(f => f.isUploaded).map((f) => ({
            fileName: f.fileName,

            // we know we have these (!) since isUploaded === true
            contentType: f.contentType!,
            url: f.url!
        }))
    ];
    const hasAttachmentsOrUploading = attachments.length || uploadingAttachments.length;

    const actions: IAction[] = [];
    if (canDelete) {
        actions.push({ Icon: DeleteIcon, role: 'destructive', text: `Delete Note`, handler: deleteNote, 'data-type': 'delete-button' });
    }

    const attachmentList = (
        <AttachmentList
            note={note}
            canEdit={canEdit}
            noteTarget={noteTarget}
            noteChanged={noteChanged}
            attachments={attachments}
            uploadingAttachments={uploadingAttachments} />
    );

    const hasTemplates = includeTemplates && templates.length > 0;
    const textAreaPlaceholder = hasTemplates ? 'Or, just start writing...' : 'Start writing here…';

    let showTitle = hasTitle;

    if (!canEdit && !note.title){
        showTitle = false;
    }

    return (
        <div data-type="note-form" data-note-id={note.noteId} className={classes.wrapper}>
            {(!excludeMenu && actions.length > 0) || (hasPrivateCheckbox && canEdit) ? (
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    {hasPrivateCheckbox && canEdit ? (
                        <div style={{height: 36}}>
                            <PrivateCheckbox data-id="notes-visibility-checkbox" checked={note.isPrivate || false} onChange={onPrivacyChanged} />
                        </div>
                    ) : <>&nbsp;</>}
                    {!excludeMenu && actions.length > 0 &&
                        <div style={{marginTop: -8, height: 36}}>
                            <Menu actions={actions} />
                        </div>
                    }
                </div>
            ) : null}

            {hasTemplates && (
                <div style={{marginLeft: -17}}>
                    <NoteTemplatePicker
                        note={note}
                        noteTarget={noteTarget}
                        onTemplateClicked={({ body, focusLocation, useLabelForTitle, label }, append) => {
                            const change = {
                                ...note,
                                html: append ? `${textareaRef.current!.value}\n\n${body}` : body
                            };

                            if (useLabelForTitle && !change.title){
                                change.title = label;
                            }
                            else if (useLabelForTitle && change.title && !append){
                                // if they are appending, we should leave the title alone
                                // otherwise they chose replace (this is our best signal they did that)
                                // or they had their own title, and we're going to replace it :(
                                // but to improve that we need to get 'replace' to buggle up so we know
                                // which does not appear to worth it at this time
                                change.title = label;
                            }

                            noteChanged(change, label);
                            placeCursor(textareaRef, focusLocation, append);
                        }}
                    />
                </div>
            )}

            {showTitle &&
                <TextArea
                    autoGrow
                    rows={1}
                    name="title"
                    label="Title"
                    placeholder="Title"
                    textareaRef={titleRef}
                    readOnly={!canEdit}
                    value={note.title}
                    onInput={(value) => onNoteTitleChanged(value)}
                />
            }

            <IonTextarea
                fill="outline"
                data-id="notes"
                ref={textareaRef}
                rows={6}
                autoGrow
                // debounce?
                value={note.html}
                readonly={!canEdit}
                onIonInput={onNoteHtmlChanged}
                labelPlacement="stacked"
                label={hasTemplates ? undefined : 'Your Notes'}
                placeholder={canEdit ? textAreaPlaceholder : ``}
            />

            {/* don't show if they can't edit (is a viewer) and there are no attachments to view */}
            {(canEdit || attachments.length > 0 || uploadingAttachments.length > 0) && (
                <div className={classes.attachmentArea}>
                    <DrawerSectionHeading text="Attachments">
                        {canEdit && (
                            <PaywallAwareButton
                                data-id="add-attachment-button"
                                requiresInnerhivePlus={addAttachmentRequiresInnerhivePlus}
                                fill="clear"
                                onClick={open}
                                triggerSource="attachment limit"
                            >
                                <PaperclipIcon />&nbsp;Add
                            </PaywallAwareButton>
                        )}

                    </DrawerSectionHeading>

                    <Divider />

                    <div
                        data-id="add-attachments"
                        style={isDesktop ? {marginBottom: 6} : {display: 'none'}}
                        className={!hasAttachmentsOrUploading && isDesktop ? classes.addAttachments : undefined}
                    >
                        <div {...getRootProps({className: isDragAccept ? classes.dragAccept : classes.dropZone})}>
                            <input {...getInputProps()} data-type="dropzone" />

                            {!hasAttachmentsOrUploading && (
                                <>
                                    <div className={classes.browseLink} style={{display: 'inline-block'}} onClick={open}>
                                        <UploadIcon size="medium" />
                                    </div>
                                    <div style={{marginLeft: 4, display: 'inline-block'}}>
                                        Drag and drop or <div className={classes.browseLink} style={{display: 'inline-block'}} onClick={open}>Add</div>
                                    </div>
                                </>
                            )}

                            {attachmentList}
                        </div>
                    </div>

                    {/* on mobile, since the add-attachments area is hidden, show the attachment list */}
                    {!isDesktop && (
                        attachments.length ? attachmentList : (
                            <div>
                                <LinkText onClick={open}>Add</LinkText> relevant files, photos and more.
                            </div>
                        )
                    )}
                </div>
            )}

            {/* Tags section */}
            <TagsField
                value={note.tags || []}
                onChange={onTagsChanged}
                disabled={!canEdit}
                entityType="note"
            />
        </div>
    );
};
